import type { AppSnmp, SnmpAuthenticationType } from '@ipfabric/utils';
import type { QueryClient } from '@tanstack/react-query';
import type { NavigateFunction } from 'react-router-dom';
import type {
  CommandModel,
  CommandModelCommandContext,
  CommandModelCommandDispatcher,
  CommandModelCommandFactory,
  CommandModelMergedState,
} from 'utils/hooks/useCommandModel';
import type { ShowNotificationCommand } from 'utils/hooks/useNotifications';
import type { ApiHook } from 'utils/request/useApi';

export interface Snmp extends Required<Omit<AppSnmp, 'authenticationType'>> {
  authenticationType: SnmpAuthenticationType;
  version: '2c' | '3';
}

export interface SnmpState {
  values: Snmp;
  loading: boolean;
  submitting: boolean;
}

export interface SnmpAdditionalCommandContext {
  api: ApiHook;
  showNotification: (command: ShowNotificationCommand) => void;
  navigate: NavigateFunction;
  queryClient: QueryClient;
}

type State = SnmpState;
type CommandContext = SnmpAdditionalCommandContext;
// biome-ignore lint/complexity/noBannedTypes: props is absent
type OuterState = {};

export type SnmpCommandContext = CommandModelCommandContext<
  State,
  CommandContext,
  OuterState
>;
export type SnmpCommandFactory<TCommandArgs = never> =
  CommandModelCommandFactory<State, CommandContext, OuterState, TCommandArgs>;
export type SnmpDispatch = CommandModelCommandDispatcher<
  State,
  CommandContext,
  OuterState
>;
export type SnmpMergedState = CommandModelMergedState<State, OuterState>;
export type SnmpModel = CommandModel<State, CommandContext, OuterState>;

export const createInitialSnmpState = (): SnmpState => ({
  values: prepareDataForSnmpForm(),
  loading: false,
  submitting: false,
});

export const prepareDataForSnmpForm = (snmp?: Snmp): Snmp => ({
  hostIp: snmp?.hostIp ?? [],
  version: snmp?.version ?? '2c',
  authenticationType: snmp?.authenticationType ?? 'SHA',
  locality: snmp?.locality ?? '',
  username: snmp?.username ?? '',
  systemContact: snmp?.systemContact ?? '',
  communityString: snmp?.communityString ?? '',
  enabled: snmp?.enabled ?? false,
  encryptionPassphrase: snmp?.encryptionPassphrase ?? '',
  passphrase: snmp?.passphrase ?? '',
});
